import { useEffect } from "react";
import { useShallow } from "zustand/shallow";
import { Location } from "react-router";
import { App } from "@capacitor/app";
import { useQueryClient } from "@tanstack/react-query";

import { DEEP_LINK_GOOGLE } from "@/routes/public/SignInRoute";
import { setUserSession } from "@/services/api/utils/auth";
import { useBoundStore } from "@/app/store";

function isGoogleSignInUrl(rawUrl: string) {
  return rawUrl.startsWith(DEEP_LINK_GOOGLE);
}

export function useNativeApp() {
  const { setDevice, setRedirect } = useBoundStore(
    useShallow((state) => ({ setDevice: state.device.set, setRedirect: state.navigation.setRedirect }))
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    (async () => {
      App.addListener("appUrlOpen", async (data) => {
        const url = new URL(data.url);
        const hashParams = new URLSearchParams(url.hash);

        if (isGoogleSignInUrl(data.url)) {
          setUserSession(hashParams);
          return;
        }

        setRedirect({
          location: generateLocation(url.href.replace(url.origin, "")),
          triggered: false,
        });
      });

      // This also works for web platforms
      App.addListener("appStateChange", ({ isActive }) => {
        setDevice({ isActive });

        // Invalidate queries when app comes to foreground
        if (isActive) {
          queryClient.invalidateQueries();
        }
      });
    })();

    return () => {
      (async () => App.removeAllListeners())();
    };
  }, [setDevice, setRedirect, queryClient]);

  return null;
}

function generateLocation(url: string): Location {
  const [pathnameAndSearch, hash] = url.split("#");
  const [pathname, search] = pathnameAndSearch.split("?");

  return {
    hash: hash ? `#${hash}` : "",
    key: Math.random().toString(36).substring(2, 8),
    pathname,
    search: search ? `?${search}` : "",
    state: null,
  };
}
